import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useEffect} from 'react';
import {useLocation, useParams} from 'react-router';
import {Redirect} from 'react-router-dom';

import {
  OLD_REPORT_PAGE_EDIT_PATH,
  OLD_REPORT_PAGE_VIEW_PATH,
} from '../../routes/paths';
import * as Analytics from '../../services/analytics';
import {PanelBankContextProvider} from '../../state/panelbank/context';
import {useAdminModeActive} from '../../util/admin';
import {redirectToSignupThenBackHere} from '../../util/auth';
import {useIsGalleryAdmin, useIsGalleryReport} from '../../util/gallery';
import {parseReportParams} from '../../util/report';
import * as Urls from '../../util/urls';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import FC from '../FC';
import {NoAccessPage} from '../NoAccessPage';
import {InstrumentedFloatingWaveLoader} from './../../components/utility/InstrumentedLoader';
import {useReportMode} from './hooks';
import {ReportPageContainer} from './ReportPageContainer';
import {ReportPageContextProvider} from './ReportPageContext';
import {ReportMode} from './types';
import {Weave1PanelContextProvider} from './Weave1PanelContext';

export type ReportPageParams = {
  entityName: string;
  projectName?: string;
  reportNameAndID: string;
  draftId?: string;
};

export type ReportPageRouterProps = {
  match: {
    path: string;
    params: {
      draftId?: string;
    };
  };
};

export function isValidViewID(base64: string): boolean {
  if (base64.length % 4 !== 0) {
    return false;
  }
  try {
    // we use the URL safe version of base64, like the backend
    const decoded = atob(base64);
    if (decoded.indexOf(':') === -1) {
      return false;
    }
    const [viewString, numericID] = decoded.split(':', 2);
    if (viewString !== 'View' || !/^-?\d+$/.test(numericID)) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error decoding draftId', error);
    return false;
  }
}

const ReportPageRouter = ({match: {path}}: ReportPageRouterProps) => {
  const urlParams = useParams<ReportPageParams>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const {entityName, projectName, reportNameAndID} = urlParams;

  const {reportId, reportName, error} = parseReportParams(reportNameAndID);
  const reportMode = useReportMode(path);
  const draftId = queryParams.get('draftId');

  useEffect(() => {
    // Note: submitting a report will cause this event to fire
    Analytics.reportPageVisited({
      reportId,
      reportMode: reportMode === ReportMode.IS_EDITING ? 'edit' : 'view',
      entityName,
      projectName,
    });
  }, [entityName, projectName, reportId, reportMode]);

  const isAdmin = useAdminModeActive();
  const isFullyConnectedAdmin = useIsGalleryAdmin();

  // Gallery spec is a single JSON and is in its own database
  const shouldSkipQuery = reportId == null;
  const {
    loading: isWBFeaturedReportLoading,
    isGalleryReport: isWBFeaturedReport,
  } = useIsGalleryReport(reportId ?? '', shouldSkipQuery);

  if (reportId == null || reportName == null || error) {
    // Redirect older paths to /reportlist and remove this logic after a few months
    if (
      (path === OLD_REPORT_PAGE_VIEW_PATH ||
        path === OLD_REPORT_PAGE_EDIT_PATH) &&
      entityName != null &&
      projectName != null
    ) {
      return <Redirect to={Urls.reportList({entityName, projectName})} />;
    }
    // Get URL query params
    const queryParams = new URLSearchParams(window.location.search);
    // If `authRedirect` is set, redirect to the login page
    if (queryParams.get('authRedirect')) {
      // Redirect back to the current page after login (w/out query params)
      redirectToSignupThenBackHere(true);
      return null;
    } else {
      return <NoAccessPage />;
    }
  }

  if (draftId != null && !isValidViewID(draftId) && projectName != null) {
    return (
      <Redirect
        to={Urls.reportView({
          entityName,
          projectName,
          reportID: reportId,
          reportName,
        })}
      />
    );
  }

  if (isWBFeaturedReportLoading) {
    return (
      <TailwindContents>
        <InstrumentedFloatingWaveLoader
          size="huge"
          name="report-page-router-loader"
        />
      </TailwindContents>
    );
  }

  // If we are editing a WBFeatured report, we need to go through the report logic.
  // Otherwise, go to FC for viewing WBFeatured reports.
  if (
    isWBFeaturedReport &&
    (!isAdmin || isFullyConnectedAdmin) &&
    reportMode === ReportMode.IS_VIEWING
  ) {
    return <FC />;
  }

  // We want to make sure that only report related routes reach this
  // context provider because it queries for view spec and we need a
  // valid report id to do so.
  return (
    <ReportPageContextProvider
      path={path}
      entityName={entityName}
      projectName={projectName}
      reportId={reportId}
      reportName={reportName}
      draftId={draftId}
      isWBFeaturedReport={isWBFeaturedReport}>
      <PanelBankContextProvider>
        <Weave1PanelContextProvider>
          <ReportPageContainer />
        </Weave1PanelContextProvider>
      </PanelBankContextProvider>
    </ReportPageContextProvider>
  );
};

export default ReportPageRouter;
